import { useEffect, useRef, useState } from "react";
import styles from "./ManageAccessModal.module.scss";
import { Typography } from "@/frontend/shared/ui/Typography";
import { SquareButton } from "@/frontend/shared/ui/Button";
import { CaretDownIcon, CaretRightIcon, CheckIcon, TrashIcon, XIcon } from "@/frontend/shared/assets/icons";
import { Modal } from "@/frontend/shared/ui/Modal";
import clsx from "clsx";
import { useOutsideClick } from "@/frontend/shared/lib/hooks/useOutsideClick";
import { Portal } from "@/frontend/shared/ui/Drawer/components/Portal";
import { useQuery } from "@tanstack/react-query";
import { getViewRequests } from "../../model/services/getViewRequests";
import { getViewRequestsPendingOnly } from "../../model/services/getViewRequestsPendingOnly";
import { postApproveRequest } from "../../model/services/postApproveRequest";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AxiosError } from "axios";
import { postRejectRequest } from "../../model/services/postRejectRequest";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { patchRequestAccess, TAccess } from "../../model/services/patchRequestAccess";
import { DropshadowPortalMenu } from "@/frontend/shared/ui/DropshadowPortalMenu/DropshadowPortalMenu";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { UsersAvatar } from "@/frontend/shared/ui/UsersAvatar/UsersAvatar";

type TManageAccessModal = {
  isOpen: boolean;
  onClose: () => void;
};

export type TInvestor = {
  firstName: string;
  lastName: string;
  profileImageUrl: string;
  email: string;
  organization: {
    name: string;
    id: number;
    profilePhotoImgUrl: string;
  };
};

export type TAccessPerson = {
  id: number;
  investor: TInvestor;
  isApproved: boolean;
  access: string;
};

export enum EContent {
  PEOPLE = "People",
  REQUESTS = "Requests"
}

export function ManageAccessModal({ isOpen, onClose }: TManageAccessModal) {
  const [menuOpen, setMenuOpen] = useState<{ [key: string]: boolean }>({});
  const [requestMenuOpen, setRequestMenuOpen] = useState<{ [key: string]: boolean }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownRequestRef = useRef<HTMLDivElement>(null);
  const elPositionRefs = useRef<HTMLDivElement[]>([]);
  const peopleListRef = useRef<HTMLDivElement>(null);
  const [shownContent, setShownContent] = useState<EContent>(EContent.PEOPLE);
  const requestPositionRefs = useRef<HTMLDivElement[]>([]);
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] = useState({ isOpen: false, id: 0 });
  const [isLoadingChangeAccess, setIsLoadingChangeAccess] = useState({ isLoading: false, id: 0 });

  const {
    data: accessPersons,
    refetch: refetchAccessPersons,
    isLoading: isLoadingAccessPersons
  } = useQuery<TAccessPerson[]>({
    queryKey: ["accessPersons", isOpen],
    queryFn: async () => {
      const res = await getViewRequests();

      return res.filter((item: TAccessPerson) => item.isApproved);
    },
    enabled: isOpen,
    refetchOnWindowFocus: false,
    gcTime: 0
  });

  const {
    data: pendingRequestPersons,
    refetch: refetchPendingRequestPersons,
    isLoading: isLoadingPendingRequest
  } = useQuery<TAccessPerson[]>({
    queryKey: ["pendingRequests", isOpen],
    queryFn: getViewRequestsPendingOnly,
    enabled: isOpen,
    refetchOnWindowFocus: false,
    gcTime: 0
  });

  function refetchAll() {
    refetchAccessPersons();
    refetchPendingRequestPersons();
  }

  useOutsideClick(dropdownRef, () => {
    if (menuOpen) {
      setMenuOpen({});
    }
  });

  useOutsideClick(dropdownRequestRef, () => {
    if (requestMenuOpen) {
      setRequestMenuOpen({});
    }
  });

  function toggleMenu(id: number) {
    setMenuOpen((prev) => {
      const newState = { ...prev };
      for (const key in newState) {
        if (key !== id.toString()) {
          newState[key] = false;
        }
      }
      newState[id] = !newState[id];
      return newState;
    });
  }

  function toggleRequestMenu(id: number) {
    setRequestMenuOpen((prev) => {
      const newState = { ...prev };
      for (const key in newState) {
        if (key !== id.toString()) {
          newState[key] = false;
        }
      }
      newState[id] = !newState[id];
      return newState;
    });
  }

  const isMenuOpen = (menuOpen && Object.keys(menuOpen).length > 0) || false;

  useEffect(() => {
    if ((isRequestMenuOpen && peopleListRef.current) || (isMenuOpen && peopleListRef.current)) {
      peopleListRef.current.style.overflow = "hidden";
    } else if (peopleListRef.current) {
      peopleListRef.current.style.overflow = "auto";
    }
  }, [menuOpen, peopleListRef, requestMenuOpen]);

  const isRequestMenuOpen = (requestMenuOpen && Object.keys(requestMenuOpen).length > 0) || false;

  async function changePersonAccess({ id, access }: { id: number; access: TAccess }) {
    try {
      if (pendingRequestPersons?.some((person) => person.id === id)) {
        setIsLoadingChangeAccess({ isLoading: true, id });
      }
      setMenuOpen({});
      setRequestMenuOpen({});
      await patchRequestAccess({ id, access });
      snackbarConstructor(`Access changed`, "normal");
    } catch (e) {
      reportErrorToSentry(e as AxiosError, "MANAGE_ACCESS_MODAL: /access-controls/${id}");
      snackbarConstructor("Error changing access", "error");
    } finally {
      refetchAll();
      setIsLoadingChangeAccess({ isLoading: false, id: 0 });
    }
  }

  function dropshadowOptionsShare(tab: number, id: number) {
    return [
      <div
        key="rename"
        className={styles.dropshadowOption}
        onClick={() => changePersonAccess({ id, access: "Profile" })}
      >
        <Typography variant="label-5" color="greyscale800">
          Profile
        </Typography>
      </div>,
      <div key="export" className={styles.dropshadowOption} onClick={() => changePersonAccess({ id, access: "Full" })}>
        <Typography variant="label-5" color="greyscale800">
          Profile & Data Room
        </Typography>
      </div>,
      <div key="export" className={styles.dropshadowOptionContainer}>
        <div className={styles.optionsDivider} />
        <div className={styles.dropshadowOption} onClick={() => onRemovePerson(id)}>
          <TrashIcon className={styles.trashIcon} />
          <Typography variant="label-5" color="greyscale600">
            Remove
          </Typography>
        </div>
      </div>
    ];
  }

  function dropshadowOptions(tab: number, id: number) {
    return [
      <div
        key="rename"
        className={styles.dropshadowOption}
        onClick={() => changePersonAccess({ id, access: "Profile" })}
      >
        <Typography variant="label-5" color="greyscale800">
          Profile
        </Typography>
      </div>,
      <div key="export" className={styles.dropshadowOption} onClick={() => changePersonAccess({ id, access: "Full" })}>
        <Typography variant="label-5" color="greyscale800">
          Profile & Data Room
        </Typography>
      </div>
      // <div key="export" className={styles.dropshadowOptionContainer}>
      //   <div className={styles.optionsDivider} />
      //   <div className={styles.dropshadowOption} onClick={() => onRemovePerson(id)}>
      //     <TrashIcon />
      //     <Typography variant="label-5" color="greyscale800">
      //       Remove
      //     </Typography>
      //   </div>
      // </div>
    ];
  }

  function onCloseModal() {
    if (shownContent === EContent.REQUESTS) {
      setShownContent(EContent.PEOPLE);
      requestPositionRefs.current = [];
      elPositionRefs.current = [];
      setMenuOpen({});
      setRequestMenuOpen({});
    } else {
      onClose();
      setShownContent(EContent.PEOPLE);
      elPositionRefs.current = [];
      requestPositionRefs.current = [];
      setMenuOpen({});
      setRequestMenuOpen({});
    }
  }

  async function onApproveRequest(id: number) {
    try {
      await postApproveRequest({ id });
      snackbarConstructor(`Request approved`, "normal");
      setShownContent(EContent.PEOPLE);
    } catch (e) {
      console.error(e);
      reportErrorToSentry(e as AxiosError, "MANAGE_ACCESS_MODAL: /access-controls/${id}/approve");
      snackbarConstructor("Error approving request", "error");
    } finally {
      refetchAll();
    }
  }

  async function onRejectRequest(id: number) {
    try {
      if (pendingRequestPersons?.some((person) => person.id === id)) {
        await changePersonAccess({ id, access: null });
      } else {
        await postRejectRequest({ id });
      }
      snackbarConstructor(`Access denied`, "normal");
    } catch (e) {
      console.error(e);
      reportErrorToSentry(e as AxiosError, "MANAGE_ACCESS_MODAL: /access-controls/${id}/decline");
      snackbarConstructor("Error declining request", "error");
    } finally {
      refetchAll();
      setIsOpenDeleteConfirmation({ isOpen: false, id: 0 });
    }
  }

  function onRemovePerson(id: number) {
    setIsOpenDeleteConfirmation({ isOpen: true, id });
    setMenuOpen({});
  }

  function onRemove(id: number) {
    setIsOpenDeleteConfirmation({ isOpen: true, id });
  }

  function getInvestorImageElement(pendingRequestPersons: TAccessPerson[] | undefined) {
    if (pendingRequestPersons && pendingRequestPersons.length > 1) {
      if (pendingRequestPersons[1]?.investor.profileImageUrl === "") {
        return (
          <img src={pendingRequestPersons[1].investor.profileImageUrl} alt="people" className={styles.secondPic} />
        );
      } else {
        return <div className={styles.secondPic} />;
      }
    }
    return null;
  }

  function content() {
    switch (true) {
      case isLoadingAccessPersons || isLoadingPendingRequest:
        return (
          <div className={styles.loaderContainer}>
            <Loader />
          </div>
        );

      case shownContent === EContent.PEOPLE:
        return (
          <div className={styles.container} id={"manageAccessModal"}>
            {/* <div className={styles.inputContainer}>
              <Input
                className={styles.input}
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                placeholder="Email, comma separated"
                inputSize="small"
              />
              <NormalButton className={styles.inviteBtn} size="medium" onClick={() => {}}>
                <Typography variant="label-4" color="greyscale100">
                  Invite
                </Typography>
              </NormalButton>
            </div> */}
            {pendingRequestPersons && pendingRequestPersons?.length > 0 && (
              <div className={styles.infoContainer} onClick={() => setShownContent(EContent.REQUESTS)}>
                <div className={styles.info}>
                  <div className={styles.picsContainer}>
                    {pendingRequestPersons[0]?.investor.profileImageUrl ? (
                      <img
                        src={pendingRequestPersons[0]?.investor.profileImageUrl}
                        alt="people"
                        className={styles.pic}
                      />
                    ) : (
                      <div className={styles.pic} />
                    )}
                    {getInvestorImageElement(pendingRequestPersons)}
                  </div>
                  <Typography
                    variant="body-2"
                    color="greyscale900"
                    className={clsx(pendingRequestPersons && pendingRequestPersons?.length > 1 && styles.infoText)}
                  >
                    {pendingRequestPersons?.length}{" "}
                    {`Person${
                      pendingRequestPersons && pendingRequestPersons?.length > 1 ? "s" : ""
                    } requested data access`}
                  </Typography>
                </div>
                <CaretRightIcon className={styles.caretIcon} />
              </div>
            )}
            <div className={styles.divider} />
            <div className={styles.peopleList} ref={peopleListRef}>
              {accessPersons?.map((person) => {
                const { organization, firstName, lastName, profileImageUrl } = person.investor;
                return (
                  <div key={person.id} className={styles.peopleContainer}>
                    <div className={styles.leftContainer}>
                      <UsersAvatar profileImg={profileImageUrl} nameInitials={firstName[0] + lastName[0]} />
                      <div className={styles.peopleInfo}>
                        <Typography variant="label-4" color="greyscale900">
                          {firstName} {lastName}
                        </Typography>
                        <Typography variant="body-3" color="greyscale600">
                          {organization.name}
                        </Typography>
                      </div>
                    </div>
                    <div className={styles.rightContainer}>
                      <Typography variant="label-5" color="greyscale500">
                        {person.isApproved ? "Can access" : "Request access"}
                      </Typography>
                      <div
                        className={styles.accessType}
                        onClick={() => toggleMenu(person.id)}
                        ref={(el) => el && (elPositionRefs.current[person.id] = el)}
                      >
                        <Typography variant="body-3" color="greyscale700">
                          {person.access === "Profile" ? "Profile" : "Profile & Data Room"}
                        </Typography>
                        <CaretDownIcon
                          className={clsx(styles.caretIcon, menuOpen[person.id] && styles.caretIconOpen)}
                        />
                      </div>
                      <DropshadowPortalMenu
                        anchorEl={elPositionRefs.current[person.id]}
                        options={dropshadowOptionsShare(person.id, person.id)}
                        isOpen={menuOpen[person.id]}
                        onClose={() => setMenuOpen((prev) => ({ ...prev, [person.id]: false }))}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case shownContent === EContent.REQUESTS:
        return (
          <div className={styles.requestsContainer} id={"manageAccessModal"}>
            <div className={styles.requestsLabelsContainer}>
              <Typography color="greyscale600" variant="label-5">
                Name
              </Typography>
              <Typography color="greyscale600" variant="label-5">
                Request
              </Typography>
            </div>
            <div className={styles.requestsContentContainer} ref={peopleListRef}>
              {pendingRequestPersons?.map((person) => {
                const { organization, firstName, lastName, profileImageUrl } = person.investor;

                return (
                  <div key={person.id} className={styles.peopleRequestContainer}>
                    <div className={styles.leftRequestContainer}>
                      {profileImageUrl ? (
                        <img src={profileImageUrl} alt="profile" className={styles.peopleRequestImage} />
                      ) : (
                        <div className={styles.peopleRequestImage}>
                          <Typography variant="label-4" color="greyscale600">
                            {firstName.slice(0, 1)}
                            {lastName.slice(0, 1)}
                          </Typography>
                        </div>
                      )}
                      <div className={styles.peopleRequestInfo}>
                        <Typography variant="label-4" color="greyscale900">
                          {firstName} {lastName}
                        </Typography>
                        <Typography variant="body-3" color="greyscale600">
                          {organization.name}
                        </Typography>
                      </div>
                    </div>
                    <div className={styles.rightRequestContainer}>
                      <div
                        className={styles.accessType}
                        onClick={() => toggleRequestMenu(person.id)}
                        ref={(el) => el && (requestPositionRefs.current[person.id] = el)}
                      >
                        <Typography variant="body-3" color="greyscale700">
                          {person.access === "Profile" ? "Profile" : "Profile & Data Room"}
                        </Typography>
                        <CaretDownIcon
                          className={clsx(styles.caretIcon, requestMenuOpen[person.id] && styles.caretIconOpen)}
                        />
                      </div>
                      {requestMenuOpen[person.id] && (
                        <Portal containerId={"manageAccessModal"}>
                          <div
                            ref={dropdownRequestRef}
                            className={styles.dropshadowRequestContainer}
                            style={{
                              top: requestPositionRefs.current[person.id]?.getBoundingClientRect().top - 190
                            }}
                          >
                            {dropshadowOptions(person.id, person.id)}
                          </div>
                        </Portal>
                      )}
                    </div>
                    <div className={styles.buttonsContainer}>
                      <SquareButton
                        className={styles.shareButton}
                        onClick={() => onApproveRequest(person.id)}
                        isDisabled={isLoadingChangeAccess.id === person.id}
                      >
                        <CheckIcon />
                      </SquareButton>
                      <SquareButton
                        variant="secondary"
                        className={styles.deleteButton}
                        onClick={() => onRemove(person.id)}
                        isDisabled={isLoadingChangeAccess.id === person.id}
                      >
                        <XIcon />
                      </SquareButton>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseModal}
      title={shownContent === EContent.PEOPLE ? "Share" : "Requests"}
      width={"560px"}
      className={styles.modal}
    >
      {content()}
      <ConfirmationModal
        isOpen={isOpenDeleteConfirmation.isOpen}
        onClose={() => setIsOpenDeleteConfirmation({ isOpen: false, id: 0 })}
        onConfirm={() => onRejectRequest(isOpenDeleteConfirmation.id)}
        content={"Are you sure you want to deny access?"}
        title={"Deny Access"}
        confirmButtonText={"Deny"}
        cancelButtonText={"Cancel"}
        dangerConfirm
      />
    </Modal>
  );
}
